import React, { useState, useEffect } from 'react';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useFormsTranslations } from '@/hooks/useFormsTranslations';
import { logError } from '@/utils/errorHandler';

interface StatusToggleProps {
  isActive: boolean;
  onToggle: (newStatus: boolean) => Promise<void>;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

export function StatusToggle({
  isActive,
  onToggle,
  disabled = false,
  size = 'md',
  showLabel = true,
  className,
}: StatusToggleProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [optimisticStatus, setOptimisticStatus] = useState(isActive);
  const forms = useFormsTranslations();

  // Update optimistic status when prop changes
  useEffect(() => {
    setOptimisticStatus(isActive);
  }, [isActive]);

  const handleToggle = async (checked: boolean) => {
    if (disabled || isLoading) return;

    // Optimistic update
    setOptimisticStatus(checked);
    setIsLoading(true);

    try {
      await onToggle(checked);
      const statusText = checked ? 'activated' : 'deactivated';
      toast.success(forms.product.notifications.statusUpdateSuccess(statusText), {
        duration: 2000,
      });
    } catch (error) {
      // Revert optimistic update on error
      setOptimisticStatus(isActive);
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: 'StatusToggle',
        action: 'handleToggle',
        metadata: {
          checked,
          context: 'product_status_update_failure',
        },
      });
      toast.error(forms.product.notifications.statusUpdateFailed);
    } finally {
      setIsLoading(false);
    }
  };

  const sizeClasses = {
    sm: {
      container: 'gap-2',
      badge: 'text-xs px-2 py-0.5',
      icon: 'h-3 w-3',
    },
    md: {
      container: 'gap-2.5',
      badge: 'text-xs px-2 py-1',
      icon: 'h-3.5 w-3.5',
    },
    lg: {
      container: 'gap-3',
      badge: 'text-sm px-3 py-1.5',
      icon: 'h-4 w-4',
    },
  };

  const currentSize = sizeClasses[size];

  return (
    <div
      className={cn(
        'flex items-center justify-between transition-all duration-200',
        currentSize.container,
        className
      )}
    >
      {showLabel && (
        <div className="flex items-center gap-2">
          <Badge
            className={cn(
              'transition-all duration-300 border font-medium',
              currentSize.badge,
              optimisticStatus
                ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
              isLoading && 'opacity-75'
            )}
          >
            <div className="flex items-center gap-1.5">
              {isLoading ? (
                <Loader2
                  className={cn('animate-spin', currentSize.icon)}
                  data-testid="loading-spinner"
                />
              ) : optimisticStatus ? (
                <CheckCircle className={cn('text-green-600', currentSize.icon)} />
              ) : (
                <XCircle className={cn('text-red-600', currentSize.icon)} />
              )}
              {optimisticStatus ? forms.common.status.active : forms.common.status.inactive}
            </div>
          </Badge>
        </div>
      )}

      <Switch
        checked={optimisticStatus}
        onCheckedChange={handleToggle}
        disabled={disabled || isLoading}
        className={cn(
          'transition-all duration-200',
          optimisticStatus
            ? 'data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600'
            : 'data-[state=unchecked]:bg-red-100 data-[state=unchecked]:border-red-300',
          isLoading && 'opacity-50 cursor-not-allowed',
          'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
        )}
        aria-label={forms.common.aria.toggleStatus(
          optimisticStatus
            ? forms.common.status.active.toLowerCase()
            : forms.common.status.inactive.toLowerCase()
        )}
      />
    </div>
  );
}

export default StatusToggle;
